/**
 * Shared Shell Component
 * LAYER-SHELL: Unified Shell for all portals
 *
 * Main layout wrapper providing TopBar, LeftRail (desktop), and MobileDrawer.
 * Uses React Router's Outlet for nested route content.
 *
 * Gets user role from auth context - shows ALL navigation items the user can access.
 * No portal switching needed - unified navigation experience.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { TopBar } from './TopBar';
import { LeftRail } from './LeftRail';
import { MobileDrawer } from './MobileDrawer';
import { HelpPanel } from './HelpPanel';
import { CommandPalette } from './CommandPalette';
import { AnnouncementStack } from '../AnnouncementBanner';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import { useAnnouncements } from '../../hooks/useAnnouncements';
import { useAuth } from '../../context';
import { Role } from '../../context/auth-types';
import { useTheme } from '../../hooks/useTheme';
import announcementsData from '../../data/announcements.json';
import './Shell.scss';

const MOBILE_BREAKPOINT = 768;

export type UserRole = 'CUSTOMER' | 'CASE_MANAGER' | 'COMPLIANCE' | 'ADMIN' | 'SUPER_ADMIN' | 'SUPPORT' | 'VIEWER';

/**
 * Role priority - higher number = more permissions
 * Used to determine the "effective" role when user has multiple
 */
const ROLE_PRIORITY: Record<string, number> = {
  CUSTOMER: 1,
  CASE_MANAGER: 2,
  COMPLIANCE: 2,
  SUPPORT: 2,
  VIEWER: 1,
  ADMIN: 3,
  SUPER_ADMIN: 4,
};

export interface ShellProps {
  /**
   * Whether to show the command palette (⌘K)
   * Auto-enabled for ADMIN/SUPER_ADMIN roles
   * @default false
   */
  showCommandPalette?: boolean;
  /**
   * Optional custom help content config path
   */
  helpContentPath?: string;
  /**
   * Called when user logs out
   */
  onLogout?: () => void;
}

export const Shell: React.FC<ShellProps> = ({
  showCommandPalette: showCommandPaletteProp = false,
  onLogout,
}) => {
  const { user, logout } = useAuth();

  // "View as Customer" mode — lets admins see the customer experience
  const [viewAsCustomer, setViewAsCustomer] = useState(() =>
    sessionStorage.getItem('shell-view-as-customer') === 'true'
  );

  // Navigate is used by both chord shortcuts and view toggle — hoist ref early
  const navigateRef = useRef<ReturnType<typeof useNavigate> | null>(null);

  const toggleViewAsCustomer = useCallback(() => {
    setViewAsCustomer(prev => {
      const next = !prev;
      sessionStorage.setItem('shell-view-as-customer', String(next));
      // Navigate to the appropriate default route
      setTimeout(() => {
        navigateRef.current?.(next ? '/accounts/me' : '/admin/dashboard');
      }, 0);
      return next;
    });
  }, []);

  // Determine effective role from user's roles (highest priority wins)
  const actualRole = useMemo<UserRole>(() => {
    if (!user?.roles?.length) {
      return 'CUSTOMER';
    }

    let maxPriority = 0;
    let bestRole: UserRole = 'CUSTOMER';

    for (const role of user.roles) {
      const priority = ROLE_PRIORITY[role] || 0;
      if (priority > maxPriority) {
        maxPriority = priority;
        bestRole = role as UserRole;
      }
    }

    return bestRole;
  }, [user?.roles]);

  // Only ADMIN/SUPER_ADMIN can use "View as Customer"
  const canViewAsCustomer = actualRole === 'ADMIN' || actualRole === 'SUPER_ADMIN';
  const effectiveRole: UserRole = canViewAsCustomer && viewAsCustomer ? 'CUSTOMER' : actualRole;

  // Force light mode for customers — dark mode is staff-only
  const { setTheme, theme: currentTheme } = useTheme();
  const savedThemeRef = useRef<string | null>(null);

  useEffect(() => {
    if (effectiveRole === 'CUSTOMER') {
      // Save current theme so we can restore it when exiting customer view
      if (savedThemeRef.current === null) {
        savedThemeRef.current = currentTheme;
      }
      setTheme('light');
    } else if (savedThemeRef.current !== null) {
      // Restore previous theme when switching back to admin
      setTheme(savedThemeRef.current as any);
      savedThemeRef.current = null;
    }
  }, [effectiveRole]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-enable command palette for admin tier
  const showCommandPalette = showCommandPaletteProp ||
    effectiveRole === 'ADMIN' || effectiveRole === 'SUPER_ADMIN';
  const [isDrawerOpen, setDrawerOpen] = useState(false);
  const [isHelpOpen, setHelpOpen] = useState(false);
  const [isCommandPaletteOpen, setCommandPaletteOpen] = useState(false);
  const location = useLocation();

  // Announcements - filtered by role and route
  const { activeAnnouncements, dismiss } = useAnnouncements({
    userRole: effectiveRole,
    currentRoute: location.pathname,
    announcements: announcementsData.announcements,
  });

  // Rail collapse state persisted in localStorage
  const storageKey = 'shell-rail-collapsed';
  const [isRailCollapsed, setRailCollapsed] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved === 'true';
  });
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);

  // Ref for hamburger button to return focus on drawer close
  const menuButtonRef = useRef<HTMLButtonElement>(null);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      // Close drawer when switching to desktop
      if (!mobile && isDrawerOpen) {
        setDrawerOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isDrawerOpen]);

  // Persist rail collapse preference
  useEffect(() => {
    localStorage.setItem(storageKey, String(isRailCollapsed));
  }, [isRailCollapsed, storageKey]);

  const navigate = useNavigate();
  navigateRef.current = navigate;
  // Track "g" chord prefix for Gmail-style navigation (g,t = transactions, g,u = users, etc.)
  const chordPrefixRef = useRef<string | null>(null);
  const chordTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Command palette keyboard shortcut (⌘K / Ctrl+K) - only for admin portal
  useEffect(() => {
    if (!showCommandPalette) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandPaletteOpen(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showCommandPalette]);

  // Keyboard shortcuts: "/" to focus search, "g,X" chord navigation
  useEffect(() => {
    if (!showCommandPalette) return; // admin-only shortcuts

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' ||
                      target.tagName === 'TEXTAREA' ||
                      target.isContentEditable;

      // Don't intercept when typing in inputs (unless Escape)
      if (isInput) return;
      // Don't intercept when command palette is open
      if (isCommandPaletteOpen) return;

      // "/" to focus the page search input
      if (e.key === '/') {
        const searchInput = document.querySelector<HTMLInputElement>(
          '.search-box, .cm-dashboard__search, .compliance-dashboard__search, [data-search-input]'
        );
        if (searchInput) {
          e.preventDefault();
          searchInput.focus();
          return;
        }
      }

      // "g" chord prefix
      if (e.key === 'g' && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (chordPrefixRef.current === null) {
          chordPrefixRef.current = 'g';
          if (chordTimeoutRef.current) clearTimeout(chordTimeoutRef.current);
          chordTimeoutRef.current = setTimeout(() => {
            chordPrefixRef.current = null;
          }, 800);
          return;
        }
      }

      // Second key of chord
      if (chordPrefixRef.current === 'g') {
        chordPrefixRef.current = null;
        if (chordTimeoutRef.current) clearTimeout(chordTimeoutRef.current);

        const chordRoutes: Record<string, string> = {
          't': '/admin/transactions',
          'u': '/admin/users',
          'c': '/compliance',
          'm': '/case-manager',
          'd': '/admin/dashboard',
          'r': '/admin/reports',
        };

        const route = chordRoutes[e.key];
        if (route) {
          e.preventDefault();
          navigate(route);
          return;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (chordTimeoutRef.current) clearTimeout(chordTimeoutRef.current);
    };
  }, [showCommandPalette, isCommandPaletteOpen, navigate]);

  const handleMenuClick = useCallback(() => {
    setDrawerOpen(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerOpen(false);
    // Return focus to hamburger button for accessibility
    menuButtonRef.current?.focus();
  }, []);

  // Touch swipe gestures for mobile drawer
  useSwipeGesture({
    enabled: isMobile,
    onSwipeRight: () => setDrawerOpen(true),
    onSwipeLeft: () => {
      if (isDrawerOpen) {
        handleDrawerClose();
      }
    },
  });

  const handleRailToggle = useCallback(() => {
    setRailCollapsed(prev => !prev);
  }, []);

  const handleHelpClick = useCallback(() => {
    setHelpOpen(true);
  }, []);

  const handleHelpClose = useCallback(() => {
    setHelpOpen(false);
  }, []);

  const handleCommandPaletteClose = useCallback(() => {
    setCommandPaletteOpen(false);
  }, []);

  return (
    <div className="shell">
      {/* Skip link for accessibility */}
      <a href="#main-content" className="shell__skip-link">
        Skip to main content
      </a>

      {/* Announcements above TopBar per DESIGN-DECISIONS Q14 */}
      <AnnouncementStack
        announcements={activeAnnouncements}
        onDismiss={dismiss}
      />

      <TopBar
        onMenuClick={handleMenuClick}
        onHelpClick={handleHelpClick}
        isMobile={isMobile}
        menuButtonRef={menuButtonRef}
        userRole={effectiveRole}
        onLogout={onLogout || logout}
      />

      {isMobile ? (
        <MobileDrawer
          isOpen={isDrawerOpen}
          onClose={handleDrawerClose}
          userRole={effectiveRole}
          canViewAsCustomer={canViewAsCustomer}
          viewAsCustomer={viewAsCustomer}
          onToggleViewAsCustomer={toggleViewAsCustomer}
        />
      ) : (
        <LeftRail
          isCollapsed={isRailCollapsed}
          onToggleCollapse={handleRailToggle}
          userRole={effectiveRole}
          canViewAsCustomer={canViewAsCustomer}
          viewAsCustomer={viewAsCustomer}
          onToggleViewAsCustomer={toggleViewAsCustomer}
        />
      )}

      <main
        id="main-content"
        className={`shell__content ${!isMobile && isRailCollapsed ? 'shell__content--collapsed' : ''}`}
        role="main"
      >
        <Outlet />
      </main>

      <HelpPanel isOpen={isHelpOpen} onClose={handleHelpClose} />

      {showCommandPalette && (
        <CommandPalette
          isOpen={isCommandPaletteOpen}
          onClose={handleCommandPaletteClose}
        />
      )}
    </div>
  );
};

export default Shell;
